import { useState, useCallback, useRef } from "react";

interface KnobInputProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  label: string;
  required?: boolean;
}

const KNOB_SIZE = 64;
const STROKE_WIDTH = 4;
const RADIUS = (KNOB_SIZE - STROKE_WIDTH) / 2;
const CENTER = KNOB_SIZE / 2;
// Arc from 135deg to 405deg (270deg sweep)
const START_ANGLE = 135;
const END_ANGLE = 405;
const SWEEP = END_ANGLE - START_ANGLE;

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function describeArc(cx: number, cy: number, r: number, startAngle: number, endAngle: number) {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y}`;
}

export function KnobInput({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  label,
  required,
}: KnobInputProps) {
  const [textValue, setTextValue] = useState(String(value));
  const [dragging, setDragging] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);

  const pct = Math.max(0, Math.min(1, (value - min) / (max - min)));
  const valueAngle = START_ANGLE + pct * SWEEP;
  const indicator = polarToCartesian(CENTER, CENTER, RADIUS - 8, valueAngle);

  const handleDrag = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      const svg = svgRef.current;
      if (!svg) return;
      const rect = svg.getBoundingClientRect();
      const clientX = "touches" in e ? e.touches[0]!.clientX : e.clientX;
      const clientY = "touches" in e ? e.touches[0]!.clientY : e.clientY;
      const dx = clientX - (rect.left + rect.width / 2);
      const dy = clientY - (rect.top + rect.height / 2);
      let angle = (Math.atan2(dy, dx) * 180) / Math.PI + 90;
      if (angle < 0) angle += 360;
      // Map angle to value: 135-405deg → min-max
      let normalized = (angle - START_ANGLE) / SWEEP;
      if (angle < START_ANGLE) normalized = 0;
      if (angle > 360 && angle < END_ANGLE - 360) normalized = 1;
      // Handle the dead zone at the bottom
      if (angle > END_ANGLE - 360 && angle < START_ANGLE) {
        normalized = angle < 270 ? 1 : 0;
      }
      normalized = Math.max(0, Math.min(1, normalized));
      const raw = min + normalized * (max - min);
      const stepped = Math.round(raw / step) * step;
      const clamped = Math.min(max, Math.max(min, stepped));
      onChange(clamped);
      setTextValue(String(clamped));
    },
    [min, max, step, onChange],
  );

  const handleText = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setTextValue(e.target.value);
      const n = Number(e.target.value);
      if (!isNaN(n)) {
        onChange(Math.min(max, Math.max(min, n)));
      }
    },
    [onChange, min, max],
  );

  const handleBlur = useCallback(() => {
    setTextValue(String(value));
  }, [value]);

  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-ink-2">
        {label}
        {required && <span className="ml-1 text-danger">*</span>}
      </label>
      <div className="flex items-center gap-3">
        <svg
          ref={svgRef}
          width={KNOB_SIZE}
          height={KNOB_SIZE}
          className="cursor-pointer"
          onMouseDown={(e) => {
            setDragging(true);
            handleDrag(e);
            const onMove = (ev: MouseEvent) => handleDrag(ev as unknown as React.MouseEvent);
            const onUp = () => {
              setDragging(false);
              document.removeEventListener("mousemove", onMove);
              document.removeEventListener("mouseup", onUp);
            };
            document.addEventListener("mousemove", onMove);
            document.addEventListener("mouseup", onUp);
          }}
        >
          {/* Background track */}
          <path
            d={describeArc(CENTER, CENTER, RADIUS, START_ANGLE, END_ANGLE)}
            fill="none"
            className="stroke-line-strong"
            strokeWidth={STROKE_WIDTH}
            strokeLinecap="round"
          />
          {/* Value arc */}
          {pct > 0.005 && (
            <path
              d={describeArc(CENTER, CENTER, RADIUS, START_ANGLE, valueAngle)}
              fill="none"
              className="stroke-accent"
              strokeWidth={STROKE_WIDTH}
              strokeLinecap="round"
            />
          )}
          {/* Indicator dot */}
          <circle cx={indicator.x} cy={indicator.y} r={3} className="fill-accent" />
          {/* Center value */}
          <text
            x={CENTER}
            y={CENTER + 1}
            textAnchor="middle"
            dominantBaseline="middle"
            className="fill-ink-2"
            fontSize="10"
            fontFamily="'JetBrains Mono', monospace"
          >
            {Math.round(value * 10) / 10}
          </text>
        </svg>
        <div className="flex flex-col gap-1">
          <input
            type="text"
            value={textValue}
            onChange={handleText}
            onBlur={handleBlur}
            className="w-16 rounded-lg border border-line bg-surface px-2 py-1.5 text-center text-sm text-ink focus:border-line-strong focus:outline-none"
          />
          <div className="text-[10px] text-ink-3">
            {min}–{max}
          </div>
        </div>
      </div>
    </div>
  );
}
